import { type ReactNode, useContext, useEffect } from 'react';
import { UNSAFE_NavigationContext } from 'react-router-dom';

interface UnsavedChangesGuardProps {
  active: boolean;
  children?: ReactNode;
}

interface BlockedTransition {
  retry(): void;
}

interface BlockableNavigator {
  block?: (callback: (transition: BlockedTransition) => void) => () => void;
}

export function UnsavedChangesGuard({
  active,
  children,
}: UnsavedChangesGuardProps) {
  const navigationContext = useContext(UNSAFE_NavigationContext);
  const navigator =
    navigationContext.navigator as unknown as BlockableNavigator;

  useEffect(() => {
    if (!active) return undefined;

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [active]);

  useEffect(() => {
    if (!active || !navigator.block) return undefined;

    const unblock = navigator.block(transition => {
      const shouldLeave = window.confirm(
        'You have unsaved changes. Leave this page and discard them?',
      );

      if (shouldLeave) {
        unblock();
        transition.retry();
      }
    });

    return unblock;
  }, [active, navigator]);

  return <>{children}</>;
}
